<script setup>
    import { ref, computed, onMounted, onUnmounted, nextTick } from "vue"
    import { useRoute, useRouter } from "vue-router"
    import {
        useEntrepriseStore,
        useRoleStore,
        useAppStore,
        useAuthStore,
    } from "~/stores"
    import CompanyGeneralForm from "~/components/features/Admin/CompanyGeneralForm.vue"

    const route = useRoute()
    const router = useRouter()
    const entrepriseStore = useEntrepriseStore()
    const roleStore = useRoleStore()
    const appStore = useAppStore()
    const authStore = useAuthStore()

    const drawer = ref(true)
    const isMobile = ref(false)
    const selectorOpen = ref(false)
    const selectorSearch = ref("")
    const showInactive = ref(false)
    const filterWebservice = ref(null)
    const searchFieldRef = ref(null)

    const isMac = computed(() =>
        navigator.platform?.toUpperCase().includes("MAC"),
    )
    const shortcutLabel = computed(() => (isMac.value ? "⌘K" : "Ctrl+K"))

    function updateMobile() {
        isMobile.value = window.innerWidth < 960
        if (isMobile.value) drawer.value = false
    }

    // Current company from route
    const currentCompanyId = computed(() => {
        return route.params.id ? Number(route.params.id) : null
    })

    const currentCompany = computed(() => {
        if (!currentCompanyId.value) return null
        return entrepriseStore.itemById?.[currentCompanyId.value] || null
    })

    const companyColor = computed(() => {
        const raw = currentCompany.value?.COLOR
        return raw && /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw : "#5C6BC0"
    })

    const webserviceOptions = computed(() => {
        const unique = new Map()
        entrepriseStore.items.forEach((c) => {
            if (c.WEBSERVICE_SERVER) {
                unique.set(c.WEBSERVICE_SERVER.id, c.WEBSERVICE_SERVER)
            }
        })
        return Array.from(unique.values())
    })

    const filteredCompanies = computed(() => {
        const q = selectorSearch.value.toLowerCase()
        return entrepriseStore.items
            .filter((c) => {
                const matchesSearch =
                    !q ||
                    c.NAME.toLowerCase().includes(q) ||
                    c.KEY?.toLowerCase().includes(q) ||
                    c.DATABASE_SERVER?.toLowerCase().includes(q) ||
                    c.DATABASE_NAME?.toLowerCase().includes(q) ||
                    c.LOLAP_SERVER?.toLowerCase().includes(q)
                const matchesActive = showInactive.value || c.ACTIVE
                const matchesWebservice =
                    !filterWebservice.value ||
                    c.WEBSERVICE_SERVER?.id === filterWebservice.value
                return matchesSearch && matchesActive && matchesWebservice
            })
            .sort((a, b) => (a.NAME || "").localeCompare(b.NAME || "", "fr"))
    })

    function openSelector() {
        selectorSearch.value = ""
        filterWebservice.value = null
        selectorOpen.value = true
        nextTick(() => searchFieldRef.value?.focus())
    }

    // Keyboard shortcut: Ctrl+K / Cmd+K
    function onKeydown(e) {
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
            e.preventDefault()
            if (selectorOpen.value) {
                selectorOpen.value = false
            } else {
                openSelector()
            }
        }
    }

    onMounted(async () => {
        document.addEventListener("keydown", onKeydown)
        updateMobile()
        window.addEventListener("resize", updateMobile)
        await entrepriseStore.fetchAll()
        await roleStore.fetchAll()
    })

    onUnmounted(() => {
        document.removeEventListener("keydown", onKeydown)
        window.removeEventListener("resize", updateMobile)
    })

    function selectCompany(company) {
        selectorOpen.value = false
        const companyId = company.ID
        if (
            route.name === "admin.company.telescope" ||
            route.name === "admin.company.telescope.show"
        ) {
            router.push({
                name: "admin.company.telescope",
                params: { id: companyId },
            })
        } else if (route.name === "admin.company.prodevis") {
            router.push({
                name: "admin.company.prodevis",
                params: { id: companyId },
            })
        } else if (route.name === "admin.company.show") {
            const section = route.params.section || "personnels"
            router.push({
                name: "admin.company.show",
                params: { id: companyId, section },
            })
        } else {
            router.push({
                name: "admin.company.show",
                params: { id: companyId, section: "personnels" },
            })
        }
    }

    // Creation d'une entreprise : formulaire de la section General, sans entreprise.
    const createOpen = ref(false)

    function openCreate() {
        selectorOpen.value = false
        createOpen.value = true
    }

    async function onCompanyCreated(settings) {
        createOpen.value = false
        await entrepriseStore.fetchAll()
        router.push({
            name: "admin.company.show",
            params: { id: settings.id, section: "general" },
        })
    }

    function toggleWebserviceFilter(ws) {
        filterWebservice.value = filterWebservice.value === ws.id ? null : ws.id
    }

    const companySections = [
        {
            section: "general",
            icon: "mdi-domain",
            label: "Général",
        },
        {
            section: "personnels",
            icon: "mdi-account-group",
            label: "Personnels",
        },
        {
            section: "clients",
            icon: "mdi-account-arrow-right",
            label: "Espace Client",
        },
        {
            section: "suivi",
            icon: "mdi-account-multiple-check",
            label: "Suivi Client",
        },
        { divider: true },
        { section: "smtp", icon: "mdi-email-outline", label: "SMTP" },
        { section: "fintecture", icon: "mdi-credit-card", label: "Fintecture" },
        { section: "lolia", icon: "mdi-robot-outline", label: "MCP IA Lola" },
        { section: "images", icon: "mdi-image-multiple", label: "Images" },
        { section: "cgv", icon: "mdi-file-document-check", label: "CGV" },
        { section: "fichiers", icon: "mdi-folder-search", label: "Fichiers" },
        { divider: true },
        {
            route: "admin.company.telescope",
            icon: "mdi-telescope",
            label: "Telescope",
        },
        {
            route: "admin.company.prodevis",
            icon: "mdi-email-sync",
            label: "Prodevis",
        },
    ]

    function companyNavTo(item) {
        const id = currentCompanyId.value
        if (!id) return undefined
        if (item.route) {
            return { name: item.route, params: { id } }
        }
        return {
            name: "admin.company.show",
            params: { id, section: item.section },
        }
    }

    function isCompanySectionActive(item) {
        if (item.route) {
            return (
                route.name === item.route || route.name === item.route + ".show"
            )
        }
        const currentSection = route.params.section || "personnels"
        return (
            route.name === "admin.company.show" &&
            currentSection === item.section
        )
    }
</script>
<template>
    <v-layout>
        <v-navigation-drawer
            v-model="drawer"
            width="240"
            :permanent="!isMobile"
            :temporary="isMobile"
        >
            <v-list
                nav
                density="compact"
                :disabled="!currentCompanyId"
                class="mt-1"
            >
                <template
                    v-for="(item, i) in companySections"
                    :key="item.section || item.label || `div-${i}`"
                >
                    <v-divider v-if="item.divider" class="my-1" />
                    <v-list-item
                        v-else
                        :prepend-icon="item.icon"
                        :title="item.label"
                        :to="companyNavTo(item)"
                        :active="isCompanySectionActive(item)"
                    />
                </template>
            </v-list>
        </v-navigation-drawer>

        <!-- Company header bar — full width, above drawer -->
        <v-app-bar
            flat
            :height="currentCompany ? 72 : 52"
            :order="-1"
            class="company-app-bar"
            :style="
                currentCompany
                    ? {
                          background: `linear-gradient(135deg, ${companyColor}18 0%, ${companyColor}08 100%)`,
                          borderBottom: `2px solid ${companyColor}30`,
                      }
                    : {}
            "
        >
            <div class="d-flex align-center w-100 px-3" style="height: 100%">
                <!-- Mobile menu toggle -->
                <v-btn
                    v-if="isMobile"
                    icon="mdi-menu"
                    variant="text"
                    size="small"
                    @click="drawer = !drawer"
                    class="mr-1"
                />

                <!-- App branding -->
                <img src="/Fichier.svg" alt="Lol@pp" class="app-logo" />

                <v-spacer />

                <!-- Company zone — clickable to switch, centered -->
                <div class="company-bar-zone" @click="openSelector">
                    <template v-if="currentCompany">
                        <div class="d-flex align-center justify-center">
                            <div style="min-width: 0">
                                <div
                                    class="d-flex align-center justify-center ga-2"
                                >
                                    <span
                                        class="text-h6 font-weight-bold text-truncate"
                                    >
                                        {{ currentCompany.NAME }}
                                    </span>
                                    <v-chip
                                        v-if="!currentCompany.ACTIVE"
                                        size="x-small"
                                        color="error"
                                        >Inactive</v-chip
                                    >
                                    <v-icon size="16" class="text-grey"
                                        >mdi-unfold-more-horizontal</v-icon
                                    >
                                    <span class="shortcut-badge">{{
                                        shortcutLabel
                                    }}</span>
                                </div>
                                <div
                                    class="d-flex align-center justify-center ga-3 mt-1"
                                >
                                    <span
                                        v-if="currentCompany.KEY"
                                        class="company-meta"
                                    >
                                        <v-icon size="11">mdi-key</v-icon>
                                        {{ currentCompany.KEY }}
                                    </span>
                                    <span
                                        v-if="
                                            currentCompany.WEBSERVICE_SERVER
                                                ?.description
                                        "
                                        class="company-meta"
                                    >
                                        <v-icon size="11">mdi-server</v-icon>
                                        {{
                                            currentCompany.WEBSERVICE_SERVER
                                                .description
                                        }}
                                    </span>
                                    <span
                                        v-if="currentCompany.LOLAP_SERVER"
                                        class="company-meta"
                                    >
                                        <v-icon size="11">mdi-database</v-icon>
                                        {{ currentCompany.LOLAP_SERVER }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="d-flex align-center justify-center ga-2 py-2"
                        >
                            <span class="text-body-1 text-grey"
                                >Sélectionner une entreprise</span
                            >
                            <v-icon size="16" class="text-grey"
                                >mdi-unfold-more-horizontal</v-icon
                            >
                            <span class="shortcut-badge">{{
                                shortcutLabel
                            }}</span>
                        </div>
                    </template>
                </div>

                <v-spacer />

                <v-menu>
                    <template #activator="{ props }">
                        <v-btn
                            v-bind="props"
                            icon="mdi-account-circle"
                            variant="text"
                            size="small"
                        />
                    </template>
                    <v-list density="compact" min-width="180">
                        <v-list-item disabled>
                            <v-list-item-title class="text-caption text-grey">
                                Admin Solis
                            </v-list-item-title>
                        </v-list-item>
                        <v-divider />
                        <v-list-item
                            prepend-icon="mdi-logout"
                            title="Déconnexion"
                            @click="authStore.logout()"
                        />
                    </v-list>
                </v-menu>
            </div>
            <v-progress-linear
                v-if="appStore.showLoading"
                absolute
                bottom
                height="5"
                indeterminate
            />
        </v-app-bar>

        <!-- Company selector dialog -->
        <v-dialog
            v-model="selectorOpen"
            max-width="560"
            :scrim="true"
            scroll-strategy="block"
        >
            <v-card rounded="lg" elevation="24">
                <!-- Search -->
                <v-text-field
                    ref="searchFieldRef"
                    v-model="selectorSearch"
                    placeholder="Rechercher (nom, clé, IP, base)..."
                    prepend-inner-icon="mdi-magnify"
                    variant="plain"
                    hide-details
                    autofocus
                    class="px-2"
                    @keydown.esc="selectorOpen = false"
                />

                <!-- Filter chips -->
                <div class="d-flex align-center flex-wrap ga-1 px-3 pb-2">
                    <v-chip
                        v-for="ws in webserviceOptions"
                        :key="ws.id"
                        size="small"
                        :variant="
                            filterWebservice === ws.id ? 'flat' : 'outlined'
                        "
                        :color="
                            filterWebservice === ws.id ? 'primary' : undefined
                        "
                        class="text-none"
                        @click="toggleWebserviceFilter(ws)"
                    >
                        {{ ws.name }}
                    </v-chip>
                    <v-spacer />
                    <v-chip
                        size="small"
                        :variant="showInactive ? 'flat' : 'outlined'"
                        :color="showInactive ? 'warning' : undefined"
                        @click="showInactive = !showInactive"
                    >
                        <v-icon start size="14">mdi-eye-off</v-icon>
                        Inactives
                    </v-chip>
                </div>

                <v-divider />

                <!-- Company list -->
                <div class="overflow-y-auto" style="max-height: 440px">
                    <template
                        v-for="(company, i) in filteredCompanies"
                        :key="company.ID"
                    >
                        <v-divider v-if="i > 0" />
                        <v-list-item
                            :active="company.ID === currentCompanyId"
                            :class="{ 'opacity-50': !company.ACTIVE }"
                            class="company-list-item py-3 px-4"
                            @click="selectCompany(company)"
                        >
                            <template #prepend>
                                <div
                                    class="company-color-dot mr-4"
                                    :style="{
                                        backgroundColor:
                                            company.COLOR &&
                                            /^#[0-9A-Fa-f]{6}$/.test(
                                                company.COLOR,
                                            )
                                                ? company.COLOR
                                                : '#5C6BC0',
                                    }"
                                />
                            </template>

                            <v-list-item-title
                                class="font-weight-medium text-body-1 mb-1"
                            >
                                {{ company.NAME }}
                                <v-chip
                                    v-if="!company.ACTIVE"
                                    size="x-small"
                                    color="error"
                                    class="ml-2"
                                >
                                    Inactive
                                </v-chip>
                            </v-list-item-title>
                            <v-list-item-subtitle
                                class="d-flex align-center ga-3"
                            >
                                <span v-if="company.KEY" class="company-meta">
                                    <v-icon size="12">mdi-key</v-icon>
                                    {{ company.KEY }}
                                </span>
                                <span
                                    v-if="
                                        company.WEBSERVICE_SERVER?.description
                                    "
                                    class="company-meta"
                                >
                                    <v-icon size="12">mdi-server</v-icon>
                                    {{ company.WEBSERVICE_SERVER.description }}
                                </span>
                                <span
                                    v-if="company.DATABASE_NAME"
                                    class="company-meta"
                                >
                                    <v-icon size="12">mdi-database</v-icon>
                                    {{ company.DATABASE_NAME }}
                                </span>
                            </v-list-item-subtitle>
                        </v-list-item>
                    </template>

                    <div
                        v-if="filteredCompanies.length === 0"
                        class="text-grey text-center pa-6"
                    >
                        Aucun résultat
                    </div>
                </div>

                <!-- Footer -->
                <v-divider />
                <div class="d-flex justify-center pa-2">
                    <v-btn
                        size="small"
                        variant="tonal"
                        color="primary"
                        prepend-icon="mdi-plus"
                        @click="openCreate"
                    >
                        Nouvelle entreprise
                    </v-btn>
                </div>
                <div
                    class="d-flex align-center justify-center pa-2 text-caption text-grey"
                >
                    <kbd class="shortcut-kbd">{{ shortcutLabel }}</kbd>
                    <span class="ml-1">pour ouvrir</span>
                    <span class="mx-2">·</span>
                    <kbd class="shortcut-kbd">Esc</kbd>
                    <span class="ml-1">pour fermer</span>
                </div>
            </v-card>
        </v-dialog>

        <!-- Creation d'une entreprise -->
        <v-dialog v-model="createOpen" max-width="900" scrollable>
            <CompanyGeneralForm
                v-if="createOpen"
                :customer-id="null"
                @saved="onCompanyCreated"
            />
        </v-dialog>

        <v-main>
            <router-view :key="currentCompanyId" />
        </v-main>
    </v-layout>
</template>

<style scoped>
    .app-logo {
    height: 22px;
    max-width: 60px;
    object-fit: contain;
    flex-shrink: 0;
}

.company-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: contain;
    flex-shrink: 0;
    background: white;
}

.company-bar-zone {
    cursor: pointer;
    border-radius: 8px;
    padding: 4px 8px;
    transition: background 0.15s;
}

.company-bar-zone:hover {
    background: rgba(0, 0, 0, 0.04);
}

.company-color-bar {
    width: 4px;
    height: 36px;
    border-radius: 2px;
    flex-shrink: 0;
}

.company-meta {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    gap: 4px;
}

.company-color-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex-shrink: 0;
}

.shortcut-badge {
    font-size: 11px;
    padding: 1px 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.5);
    font-family: system-ui, sans-serif;
}

.shortcut-kbd {
    font-size: 11px;
    padding: 1px 5px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: rgba(0, 0, 0, 0.04);
    font-family: system-ui, sans-serif;
}

.company-list-item {
    cursor: pointer;
}

.company-list-item:hover {
    background: rgba(0, 0, 0, 0.04);
}
</style>
